import type { FontMeta } from '@backend/lib/db/schema/moodboard'

/**
 * Parse a font file's real family name, weight, and style from its binary.
 *
 * fontkit is heavy (and only needed when a font is uploaded), so it's
 * dynamically imported — it never enters the main bundle. fontkit handles
 * ttf/otf/woff and woff2 (it bundles the decompression). Returns null on any
 * failure so callers fall back to the filename-derived name.
 */
export async function parseFontMetadata(file: File): Promise<FontMeta | null> {
  try {
    const fontkit = await import('fontkit')
    const bytes = new Uint8Array(await file.arrayBuffer())
    // fontkit's browser build accepts a Uint8Array; its types say Buffer.
    const parsed = fontkit.create(bytes as unknown as Buffer)
    // .ttc collections expose `.fonts`; take the first face.
    const font = 'fonts' in parsed ? parsed.fonts[0] : parsed
    if (!font?.familyName) return null

    const os2 = font['OS/2'] as
      | { usWeightClass?: number; fsSelection?: { italic?: boolean } }
      | undefined
    const weight = typeof os2?.usWeightClass === 'number' ? os2.usWeightClass : 400
    const italic =
      Boolean(os2?.fsSelection?.italic) ||
      (typeof font.italicAngle === 'number' && font.italicAngle !== 0) ||
      /italic|oblique/i.test(font.subfamilyName ?? '')

    return { family: font.familyName, weight, style: italic ? 'italic' : 'normal' }
  } catch (error) {
    console.warn('Failed to parse font metadata', error)
    return null
  }
}
